/**
 * Purchase Flow Mock Fixtures
 *
 * Pre-built flow states and chat histories for each of the 9 stages.
 * Used by mockPurchaseFlowService.ts to load demo scenarios.
 *
 * Each fixture includes:
 *   - flow: PurchaseFlow state at that stage
 *   - messages: ChatMessage[] history up to that point
 *   - activityEntries: tool call log
 *
 * To preview a specific stage, import the fixture and pass it to the mock service.
 */

import type {
  PurchaseFlow,
  ChatMessage,
  ActivityEntry,
  MockVendor,
  MockProduct,
  MockPOSummary,
  MockInboundSummary,
  MockInvoiceSummary,
  MockMatchResult,
  MockFlowChecklist,
  PurchaseStage,
} from '@/types/purchaseFlow';

// ============================================================================
// Shared Mock Data
// ============================================================================

export const VENDORS: MockVendor[] = [
  { vendor_code: 'V-0023', name: 'Molex Inc', tax_id: '12345678', contact: 'Kevin Lin', phone: '02-2771-3456', email: 'kevin.lin@molex.com' },
  { vendor_code: 'V-0045', name: 'TDK Corporation', tax_id: '87654321', contact: 'Amy Wang', phone: '02-2883-7890', email: 'amy.wang@tdk.com' },
  { vendor_code: 'V-0067', name: 'Murata Manufacturing', tax_id: '11223344', contact: 'David Chen', phone: '02-2567-1234', email: 'd.chen@murata.com' },
];

export const PRODUCTS: MockProduct[] = [
  // Molex Inc (V-0023)
  { part_code: 'C-0042', description: 'USB-C Connector 24pin', specification: 'USB-C, 24-pin, SMD, 10Gbps', vendor_code: 'V-0023', vendor_name: 'Molex Inc', last_purchase_price: 48.50, currency: 'TWD', lead_time_days: 14, moq: 100 },
  { part_code: 'C-0043', description: 'USB-C Receptacle Vertical', specification: 'USB-C, 24-pin, Through-hole', vendor_code: 'V-0023', vendor_name: 'Molex Inc', last_purchase_price: 52.00, currency: 'TWD', lead_time_days: 14, moq: 100 },
  { part_code: 'C-0044', description: 'FPC Connector 0.5mm 30pin', specification: 'FPC/FFC, 0.5mm pitch, 30-pin, SMD', vendor_code: 'V-0023', vendor_name: 'Molex Inc', last_purchase_price: 18.00, currency: 'TWD', lead_time_days: 14, moq: 200 },
  // TDK Corporation (V-0045)
  { part_code: 'P-0108', description: 'MLCC 0.1uF 50V 0402', specification: 'Capacitor, 100nF, 50V, X7R, 0402', vendor_code: 'V-0045', vendor_name: 'TDK Corporation', last_purchase_price: 0.85, currency: 'TWD', lead_time_days: 7, moq: 1000 },
  { part_code: 'P-0109', description: 'MLCC 10uF 16V 0805', specification: 'Capacitor, 10µF, 16V, X5R, 0805', vendor_code: 'V-0045', vendor_name: 'TDK Corporation', last_purchase_price: 2.50, currency: 'TWD', lead_time_days: 7, moq: 500 },
  { part_code: 'L-0021', description: 'Inductor 4.7uH 1A 0805', specification: 'Inductor, 4.7µH, 1A, 0805, Shielded', vendor_code: 'V-0045', vendor_name: 'TDK Corporation', last_purchase_price: 5.20, currency: 'TWD', lead_time_days: 10, moq: 500 },
  // Murata Manufacturing (V-0067)
  { part_code: 'P-0201', description: 'Resistor 10K 0402', specification: 'Resistor, 10KΩ, 1%, 0402', vendor_code: 'V-0067', vendor_name: 'Murata Manufacturing', last_purchase_price: 0.12, currency: 'TWD', lead_time_days: 7, moq: 5000 },
  { part_code: 'P-0202', description: 'Chip Resistor 100Ω 0402', specification: 'Resistor, 100Ω, 1%, 0402', vendor_code: 'V-0067', vendor_name: 'Murata Manufacturing', last_purchase_price: 0.10, currency: 'TWD', lead_time_days: 7, moq: 5000 },
  { part_code: 'F-0015', description: 'EMI Filter 0402', specification: 'EMI Filter, 0402, 50V, 2A', vendor_code: 'V-0067', vendor_name: 'Murata Manufacturing', last_purchase_price: 3.80, currency: 'TWD', lead_time_days: 10, moq: 1000 },
];

export const PO_SUMMARY: MockPOSummary = {
  po_id: 'po-mock-001',
  po_number: 'FCR-015-202603-0012',
  vendor_code: 'V-0023',
  vendor_name: 'Molex Inc',
  items: [
    { product_name: 'USB-C Connector 24pin', specification: 'USB-C, 24-pin, SMD', quantity: 500, unit_price: 46.00, currency: 'TWD' },
    { product_name: 'USB-C Receptacle Vertical', specification: 'USB-C, 24-pin, TH', quantity: 1000, unit_price: 50.00, currency: 'TWD' },
  ],
  subtotal: 73000,
  tax_amount: 3650,
  grand_total: 76650,
  currency: 'TWD',
  status: 'pending',
};

export const INBOUND_SUMMARY: MockInboundSummary = {
  inbound_id: 'ib-mock-001',
  inbound_number: 'GR-202603-0001',
  po_number: 'FCR-015-202603-0012',
  vendor_name: 'Molex Inc',
  received_date: '2026-04-08',
  items: [
    { line_no: 1, product_name: 'USB-C Connector 24pin', received_qty: 500, po_qty: 500, iqc_status: 'passed' },
    { line_no: 2, product_name: 'USB-C Receptacle Vertical', received_qty: 1000, po_qty: 1000, iqc_status: 'passed' },
  ],
  total_amount: 73000,
  currency: 'TWD',
  status: 'iqc_passed',
};

export const INVOICE_SUMMARY: MockInvoiceSummary = {
  invoice_id: 'inv-mock-001',
  invoice_number: 'XF0875002',
  vendor_name: 'Molex Inc',
  invoice_date: '2026-04-09',
  due_date: '2026-05-09',
  grand_total: 76650,
  currency: 'TWD',
  status: 'draft',
  items: [
    { product_name: 'USB-C Connector 24pin', quantity: 500, unit_price: 46.00, amount: 23000 },
    { product_name: 'USB-C Receptacle Vertical', quantity: 1000, unit_price: 50.00, amount: 50000 },
  ],
};

export const MATCH_RESULT: MockMatchResult = {
  po_number: 'FCR-015-202603-0012',
  overall_match: true,
  lines: [
    { product_name: 'USB-C Connector 24pin', po_qty: 500, received_qty: 500, invoiced_qty: 500, po_amount: 23000, invoice_amount: 23000, match: true },
    { product_name: 'USB-C Receptacle Vertical', po_qty: 1000, received_qty: 1000, invoiced_qty: 1000, po_amount: 50000, invoice_amount: 50000, match: true },
  ],
  po_total: 73000,
  inbound_total: 73000,
  invoice_total: 73000,
};

export const FLOW_CHECKLIST: MockFlowChecklist = {
  flow_id: 'fixture-flow-009',
  po_number: 'FCR-015-202603-0012',
  vendor_name: 'Molex Inc',
  checklist: {
    vendor_selected: true,
    items_confirmed: true,
    po_created: true,
    delivery_set: true,
    iqc_rules_set: true,
    goods_received: true,
    iqc_passed: true,
    invoice_created: true,
    amounts_matched: true,
    payment_complete: true,
  },
  totals: { po_total: 76650, received_total: 73000, invoiced_total: 76650 },
  currency: 'TWD',
};

export const DELIVERY_DATE = '2026-04-08';
export const PAID_DATE = '2026-04-10';

// ============================================================================
// Fixture type
// ============================================================================

export interface StageFixture {
  /** Which stage this fixture represents */
  stage: PurchaseStage;
  /** Flow state at this stage */
  flow: PurchaseFlow;
  /** Chat history accumulated up to this stage */
  messages: ChatMessage[];
  /** Activity log accumulated up to this stage */
  activity: ActivityEntry[];
}

// ============================================================================
// Helper: build flow at a given stage
// ============================================================================

function makeFlow(stage: PurchaseStage, extra: Partial<PurchaseFlow> = {}): PurchaseFlow {
  const base: PurchaseFlow = {
    flow_id: `fixture-flow-${stage}`,
    current_stage: stage,
    stage_data: {},
    stage_history: [],
    status: 'active',
    created_by: 'demo-user',
    created_at: '2026-03-24T10:00:00Z',
    updated_at: new Date().toISOString(),
  };
  return { ...base, ...extra };
}

function transition(from: PurchaseStage, to: PurchaseStage) {
  return { from, to, action: 'advance', username: 'agent', timestamp: new Date().toISOString() };
}

let _msgId = 1000;
function msg(role: ChatMessage['role'], content: string, extra: Partial<ChatMessage> = {}): ChatMessage {
  return { id: _msgId++, role, content, ...extra };
}

function tool(toolName: string, done = true): ActivityEntry {
  return {
    toolId: `fix-tool-${_msgId++}`,
    toolName,
    timestamp: Date.now(),
    done,
    isCreate: ['psi_create_vendor', 'psi_create_product', 'psi_create_purchase_order', 'psi_create_inbound', 'psi_create_invoice'].includes(toolName),
  };
}

function toolCallInfo(toolName: string) {
  return { toolId: `fix-tc-${_msgId++}`, toolName, done: true };
}

function richBlock(type: ChatMessage['richBlocks'] extends (infer R)[] | undefined ? R extends { type: infer T } ? T : never : never, data: unknown, toolCallId = `fix-rb-${_msgId++}`) {
  return { type, data, toolCallId } as NonNullable<ChatMessage['richBlocks']>[number];
}

// ============================================================================
// Stage Fixtures
// ============================================================================

/** Stage 1: Vendor Select — fresh start, agent greeting */
export const STAGE_1_VENDOR_SELECT: StageFixture = {
  stage: 'vendor_select',
  flow: makeFlow('vendor_select'),
  messages: [
    msg('assistant', 'Welcome! Who are you purchasing from?', {
      toolCalls: [toolCallInfo('psi_flow_get_status')],
    }),
  ],
  activity: [tool('psi_flow_get_status')],
};

/** Stage 1b: Vendor Select — vendors listed, awaiting selection */
export const STAGE_1B_VENDORS_LISTED: StageFixture = {
  stage: 'vendor_select',
  flow: makeFlow('vendor_select'),
  messages: [
    msg('assistant', 'Welcome! Who are you purchasing from?'),
    msg('user', 'Show me the vendor list'),
    msg('assistant', 'Here are the available vendors. Select one to proceed, or I can create a new vendor for you.', {
      toolCalls: [toolCallInfo('psi_list_vendors')],
      richBlocks: [richBlock('vendor_list', { vendors: VENDORS, total: 3 })],
      quickReplies: [{ label: 'Create new vendor', value: 'Create new vendor' }],
    }),
  ],
  activity: [tool('psi_flow_get_status'), tool('psi_list_vendors')],
};

/** Stage 2: Item Select — vendor selected, awaiting items */
export const STAGE_2_ITEM_SELECT: StageFixture = {
  stage: 'item_select',
  flow: makeFlow('item_select', {
    vendor_code: 'V-0023',
    stage_data: { vendor_code: 'V-0023', vendor_name: 'Molex Inc', vendor: { code: 'V-0023', name: 'Molex Inc' } },
    stage_history: [transition('vendor_select', 'item_select')],
  }),
  messages: [
    msg('assistant', 'Welcome! Who are you purchasing from?'),
    msg('user', 'Molex'),
    msg('assistant', '', {
      toolCalls: [toolCallInfo('psi_list_vendors')],
      richBlocks: [richBlock('vendor_list', { vendors: [VENDORS[0]], total: 1 })],
    }),
    msg('stage_banner', 'item_select'),
    msg('assistant', '**Molex Inc** selected as vendor. What items do you need to purchase?'),
  ],
  activity: [tool('psi_list_vendors'), tool('psi_flow_advance_stage')],
};

/** Stage 2b: Item Select — products found, awaiting add */
export const STAGE_2B_PRODUCTS_LISTED: StageFixture = {
  stage: 'item_select',
  flow: makeFlow('item_select', {
    vendor_code: 'V-0023',
    stage_data: { vendor_code: 'V-0023', vendor_name: 'Molex Inc' },
    stage_history: [transition('vendor_select', 'item_select')],
  }),
  messages: [
    ...STAGE_2_ITEM_SELECT.messages,
    msg('user', 'USB-C connectors'),
    msg('assistant', 'I found these parts from the AVL. Select items and specify quantities, or search for more.', {
      toolCalls: [toolCallInfo('psi_search_products')],
      richBlocks: [richBlock('product_list', { products: PRODUCTS.slice(0, 2), total: 2, query: 'USB-C' })],
    }),
  ],
  activity: [...STAGE_2_ITEM_SELECT.activity, tool('psi_search_products')],
};

/** Stage 3: Price Confirm — items added, reviewing pricing and quantities */
export const STAGE_3_PRICE_CONFIRM: StageFixture = {
  stage: 'price_confirm',
  flow: makeFlow('price_confirm', {
    vendor_code: 'V-0023',
    stage_data: {
      vendor_code: 'V-0023', vendor_name: 'Molex Inc',
      items: PO_SUMMARY.items.map((it, i) => ({ ...it, line_no: i + 1, part_code: i === 0 ? 'C-0042' : 'C-0043' })),
    },
    stage_history: [transition('vendor_select', 'item_select'), transition('item_select', 'price_confirm')],
  }),
  messages: [
    msg('assistant', '**Molex Inc** selected. What items do you need?'),
    msg('user', '500 USB-C Connectors and 1000 USB-C Receptacles'),
    msg('stage_banner', 'price_confirm'),
    msg('assistant', '**Molex Inc** — order review:\n\n1. **USB-C Connector 24pin** — qty: 500 × 46 = 23,000 TWD\n2. **USB-C Receptacle Vertical** — qty: 1,000 × 50 = 50,000 TWD\n\nSubtotal: **TWD 73,000** + 5% tax\n\nYou can view price tiers, compare other vendors, adjust quantities, remove items, or confirm to create the PO.', {
      toolCalls: [toolCallInfo('psi_flow_advance_stage')],
    }),
    msg('user', 'Show price tiers for these items'),
    msg('assistant', 'Showing price tiers from **Molex Inc**. Adjust quantities in the table to see how totals change, then apply to update the draft.', {
      toolCalls: [toolCallInfo('psi_get_price_tiers')],
      richBlocks: [richBlock('price_tier_table', {
        items: PO_SUMMARY.items.map((it, i) => ({
          part_code: i === 0 ? 'C-0042' : 'C-0043',
          product_name: it.product_name,
          specification: it.specification,
          current_qty: it.quantity,
          current_price: it.unit_price,
          currency: it.currency,
          tiers: [
            { min_qty: 1, unit_price: Math.round(it.unit_price * 1.15) },
            { min_qty: 100, unit_price: Math.round(it.unit_price * 1.05) },
            { min_qty: 500, unit_price: it.unit_price },
            { min_qty: 1000, unit_price: Math.round(it.unit_price * 0.92) },
            { min_qty: 5000, unit_price: Math.round(it.unit_price * 0.85) },
          ],
        })),
        vendor_code: 'V-0023', vendor_name: 'Molex Inc',
      })],
    }),
  ],
  activity: [tool('psi_list_vendors'), tool('psi_flow_advance_stage'), tool('psi_get_price_tiers')],
};

/** Stage 3b / Stage 4: Delivery Schedule — set date, address, payment terms before PO creation */
export const STAGE_3B_PO_CREATED: StageFixture = {
  stage: 'delivery_schedule',
  flow: makeFlow('delivery_schedule', {
    vendor_code: 'V-0023',
    stage_data: {
      vendor_code: 'V-0023', vendor_name: 'Molex Inc',
      items: PO_SUMMARY.items.map((it, i) => ({ ...it, line_no: i + 1, part_code: i === 0 ? 'C-0042' : 'C-0043' })),
    },
    stage_history: [
      transition('vendor_select', 'item_select'),
      transition('item_select', 'price_confirm'),
      transition('price_confirm', 'delivery_schedule'),
    ],
  }),
  messages: [
    ...STAGE_3_PRICE_CONFIRM.messages,
    msg('user', 'Prices confirmed, proceed to delivery'),
    msg('stage_banner', 'delivery_schedule'),
    msg('assistant', 'Set delivery details for this order. Use the buttons to set delivery date, address, and payment terms.', {
      toolCalls: [toolCallInfo('psi_flow_advance_stage')],
    }),
  ],
  activity: [...STAGE_3_PRICE_CONFIRM.activity, tool('psi_flow_advance_stage')],
};

/** Stage 4: Delivery Schedule (alias) */
export const STAGE_4_DELIVERY: StageFixture = STAGE_3B_PO_CREATED;

/** Stage 5: IQC Rule — delivery set, choosing inspection */
export const STAGE_5_IQC_RULE: StageFixture = {
  stage: 'iqc_rule',
  flow: makeFlow('iqc_rule', {
    vendor_code: 'V-0023',
    po_id: 'po-mock-001',
    stage_data: {
      vendor_code: 'V-0023', vendor_name: 'Molex Inc',
      po_id: 'po-mock-001', po_number: 'FCR-015-202603-0012', grand_total: 76650,
      expected_delivery: DELIVERY_DATE, payment_terms: '月結30天',
      items: PO_SUMMARY.items.map((it, i) => ({ ...it, line_no: i + 1 })),
    },
    stage_history: [
      transition('vendor_select', 'item_select'),
      transition('item_select', 'price_confirm'),
      transition('price_confirm', 'delivery_schedule'),
      transition('delivery_schedule', 'iqc_rule'),
    ],
  }),
  messages: [
    msg('assistant', 'Set delivery details for this order.'),
    msg('user', 'Delivery details confirmed, create PO'),
    msg('stage_banner', 'iqc_rule'),
    msg('assistant', `PO **FCR-015-202603-0012** created for **TWD 76,650**, delivery **${DELIVERY_DATE}**, address **雲騰電通 總公司**, payment **月結30天**.\n\nWhat IQC inspection level would you like?`, {
      toolCalls: [toolCallInfo('psi_create_purchase_order'), toolCallInfo('psi_update_po_status')],
      richBlocks: [richBlock('po_summary', PO_SUMMARY)],
      quickReplies: [
        { label: 'Full inspection', value: 'Full inspection for all items' },
        { label: 'Sampling', value: 'Use sampling inspection' },
        { label: 'Waive IQC', value: 'Waive inspection — trusted vendor' },
      ],
    }),
  ],
  activity: [tool('psi_create_purchase_order'), tool('psi_update_po_status'), tool('psi_flow_advance_stage')],
};

/** Stage 6: Receiving — flow was paused, user returns */
export const STAGE_6_RECEIVING: StageFixture = {
  stage: 'receiving',
  flow: makeFlow('receiving', {
    vendor_code: 'V-0023',
    po_id: 'po-mock-001',
    status: 'paused',
    stage_data: {
      vendor_code: 'V-0023', vendor_name: 'Molex Inc',
      po_id: 'po-mock-001', po_number: 'FCR-015-202603-0012', grand_total: 76650,
      expected_delivery: DELIVERY_DATE, payment_terms: '月結30天', iqc_type: 'full',
      items: PO_SUMMARY.items.map((it, i) => ({ ...it, line_no: i + 1 })),
    },
    stage_history: [
      transition('vendor_select', 'item_select'),
      transition('item_select', 'price_confirm'),
      transition('price_confirm', 'delivery_schedule'),
      transition('delivery_schedule', 'iqc_rule'),
      transition('iqc_rule', 'receiving'),
    ],
  }),
  messages: [
    msg('assistant', 'IQC set to **full** inspection. PO status is now **ordered**.\n\nThe flow is paused until the shipment arrives. When goods arrive, come back and tell me!'),
    msg('user', 'The shipment arrived today'),
  ],
  activity: [tool('psi_update_po_status'), tool('psi_flow_advance_stage')],
};

/** Stage 6b: Receiving complete — all items received + IQC passed */
export const STAGE_6B_RECEIVED: StageFixture = {
  stage: 'invoice_input',
  flow: makeFlow('invoice_input', {
    vendor_code: 'V-0023',
    po_id: 'po-mock-001',
    inbound_ids: ['ib-mock-001'],
    stage_data: {
      vendor_code: 'V-0023', vendor_name: 'Molex Inc',
      po_id: 'po-mock-001', po_number: 'FCR-015-202603-0012', grand_total: 76650,
      expected_delivery: DELIVERY_DATE, iqc_type: 'full', inbound_ids: ['ib-mock-001'],
      items: PO_SUMMARY.items.map((it, i) => ({ ...it, line_no: i + 1 })),
    },
    stage_history: [
      transition('vendor_select', 'item_select'),
      transition('item_select', 'price_confirm'),
      transition('price_confirm', 'delivery_schedule'),
      transition('delivery_schedule', 'iqc_rule'),
      transition('iqc_rule', 'receiving'),
      transition('receiving', 'invoice_input'),
    ],
  }),
  messages: [
    msg('assistant', 'The shipment arrived? Let me record the delivery.'),
    msg('assistant', '', {
      toolCalls: [toolCallInfo('psi_create_inbound'), toolCallInfo('psi_update_iqc')],
      richBlocks: [richBlock('inbound_summary', INBOUND_SUMMARY)],
    }),
    msg('stage_banner', 'invoice_input'),
    msg('assistant', 'All items received and IQC passed! Inbound **GR-202603-0001** recorded.\n\nNow let\'s handle the invoice. How would you like to proceed?', {
      quickReplies: [
        { label: 'Use PO data', value: 'Create invoice from PO data' },
        { label: 'Enter manually', value: 'I will enter the invoice details manually' },
      ],
    }),
  ],
  activity: [tool('psi_create_inbound'), tool('psi_update_iqc'), tool('psi_flow_advance_stage')],
};

/** Stage 7: Invoice Input */
export const STAGE_7_INVOICE: StageFixture = STAGE_6B_RECEIVED;

/** Stage 7b: Invoice created + three-way matched */
export const STAGE_7B_INVOICE_MATCHED: StageFixture = {
  stage: 'ap_completion',
  flow: makeFlow('ap_completion', {
    vendor_code: 'V-0023',
    po_id: 'po-mock-001',
    inbound_ids: ['ib-mock-001'],
    invoice_id: 'inv-mock-001',
    stage_data: {
      vendor_code: 'V-0023', vendor_name: 'Molex Inc',
      po_id: 'po-mock-001', po_number: 'FCR-015-202603-0012', grand_total: 76650,
      expected_delivery: DELIVERY_DATE, iqc_type: 'full',
      inbound_ids: ['ib-mock-001'], invoice_id: 'inv-mock-001', invoice_number: 'XF0875002',
      items: PO_SUMMARY.items.map((it, i) => ({ ...it, line_no: i + 1 })),
    },
    stage_history: [
      transition('vendor_select', 'item_select'),
      transition('item_select', 'price_confirm'),
      transition('price_confirm', 'delivery_schedule'),
      transition('delivery_schedule', 'iqc_rule'),
      transition('iqc_rule', 'receiving'),
      transition('receiving', 'invoice_input'),
      transition('invoice_input', 'ap_completion'),
    ],
  }),
  messages: [
    msg('user', 'Create invoice from PO data'),
    msg('assistant', '', {
      toolCalls: [toolCallInfo('psi_create_invoice')],
      richBlocks: [richBlock('invoice_summary', INVOICE_SUMMARY)],
    }),
    msg('assistant', '', {
      toolCalls: [toolCallInfo('psi_three_way_match')],
      richBlocks: [richBlock('match_table', MATCH_RESULT)],
    }),
    msg('stage_banner', 'ap_completion'),
    msg('assistant', 'Invoice **XF0875002** created. Three-way match: **all items matched** ✓\n\nReady to record payment.', {
      quickReplies: [
        { label: 'Bank transfer done', value: 'Payment was made by bank transfer' },
        { label: 'Show payment details', value: 'Show payment details and vendor bank info' },
      ],
    }),
  ],
  activity: [tool('psi_create_invoice'), tool('psi_three_way_match'), tool('psi_flow_advance_stage')],
};

/** Stage 8: AP Completion */
export const STAGE_8_AP: StageFixture = STAGE_7B_INVOICE_MATCHED;

/** Stage 9: Close — payment done, showing summary */
export const STAGE_9_CLOSE: StageFixture = {
  stage: 'close',
  flow: makeFlow('close', {
    vendor_code: 'V-0023',
    po_id: 'po-mock-001',
    inbound_ids: ['ib-mock-001'],
    invoice_id: 'inv-mock-001',
    status: 'completed',
    stage_data: {
      vendor_code: 'V-0023', vendor_name: 'Molex Inc',
      po_id: 'po-mock-001', po_number: 'FCR-015-202603-0012', grand_total: 76650,
      expected_delivery: DELIVERY_DATE, iqc_type: 'full',
      inbound_ids: ['ib-mock-001'], invoice_id: 'inv-mock-001', invoice_number: 'XF0875002',
      paid_date: PAID_DATE,
      items: PO_SUMMARY.items.map((it, i) => ({ ...it, line_no: i + 1 })),
    },
    stage_history: [
      transition('vendor_select', 'item_select'),
      transition('item_select', 'price_confirm'),
      transition('price_confirm', 'delivery_schedule'),
      transition('delivery_schedule', 'iqc_rule'),
      transition('iqc_rule', 'receiving'),
      transition('receiving', 'invoice_input'),
      transition('invoice_input', 'ap_completion'),
      transition('ap_completion', 'close'),
    ],
  }),
  messages: [
    msg('user', 'Payment was made by bank transfer'),
    msg('assistant', `Payment recorded on **${PAID_DATE}**. Let me generate the flow summary.`, {
      toolCalls: [toolCallInfo('psi_update_invoice_status')],
    }),
    msg('stage_banner', 'close'),
    msg('assistant', 'Here\'s the complete flow summary. All checks passed!', {
      toolCalls: [toolCallInfo('psi_flow_summary')],
      richBlocks: [richBlock('flow_checklist', FLOW_CHECKLIST)],
      quickReplies: [{ label: 'Close flow', value: 'Close flow' }],
    }),
  ],
  activity: [tool('psi_update_invoice_status'), tool('psi_flow_advance_stage'), tool('psi_flow_summary')],
};

// ============================================================================
// All fixtures indexed by name
// ============================================================================

export const ALL_FIXTURES: Record<string, StageFixture> = {
  '1_vendor_select':       STAGE_1_VENDOR_SELECT,
  '1b_vendors_listed':     STAGE_1B_VENDORS_LISTED,
  '2_item_select':         STAGE_2_ITEM_SELECT,
  '2b_products_listed':    STAGE_2B_PRODUCTS_LISTED,
  '3_price_confirm':       STAGE_3_PRICE_CONFIRM,
  '3b_po_created':         STAGE_3B_PO_CREATED,
  '4_delivery':            STAGE_4_DELIVERY,
  '5_iqc_rule':            STAGE_5_IQC_RULE,
  '6_receiving':           STAGE_6_RECEIVING,
  '6b_received':           STAGE_6B_RECEIVED,
  '7_invoice':             STAGE_7_INVOICE,
  '7b_invoice_matched':    STAGE_7B_INVOICE_MATCHED,
  '8_ap':                  STAGE_8_AP,
  '9_close':               STAGE_9_CLOSE,
};

/** Stage names for UI dropdown */
export const FIXTURE_LABELS: Record<string, string> = {
  '1_vendor_select':       'Stage 1: Vendor Select (greeting)',
  '1b_vendors_listed':     'Stage 1b: Vendors Listed',
  '2_item_select':         'Stage 2: Item Select',
  '2b_products_listed':    'Stage 2b: Products Listed',
  '3_price_confirm':       'Stage 3: Price Confirm (tiers + review)',
  '3b_po_created':         'Stage 4: Delivery Setup',
  '4_delivery':            'Stage 4: Delivery Schedule',
  '5_iqc_rule':            'Stage 5: IQC Rule',
  '6_receiving':           'Stage 6: Receiving (paused)',
  '6b_received':           'Stage 6b: Items Received',
  '7_invoice':             'Stage 7: Invoice Input',
  '7b_invoice_matched':    'Stage 7b: Invoice + 3-Way Match',
  '8_ap':                  'Stage 8: AP Completion',
  '9_close':               'Stage 9: Close (summary)',
};
